import {
  cn,
  Tooltip as HeroTooltip,
  type TooltipContentProps,
} from "@heroui/react";
import type { ComponentProps, PointerEvent, ReactNode } from "react";
import { useEffect, useId, useRef, useState } from "react";
import { Focusable } from "react-aria/Focusable";
import type { SlotsToClasses } from "src/utils/componentUtils";

interface TooltipBaseProps {
  content: ReactNode;
  /** Hover delay in ms. Defaults to 0 to match v2 behavior (v3 default is 700). */
  delay?: number;
  closeDelay?: number;
  placement?: TooltipContentProps["placement"];
  classNames?: SlotsToClasses<"trigger" | "content">;
  showArrow?: boolean;
  isDisabled?: boolean;
  trigger?: "hover" | "focus";
  stopPropagation?: boolean;
}

export type TooltipProps = TooltipBaseProps & {
  asChild?: boolean;
  children: ComponentProps<typeof Focusable>["children"];
};

export function Tooltip({
  content,
  children,
  delay = 0,
  closeDelay,
  placement,
  classNames,
  showArrow,
  isDisabled,
  trigger,
  stopPropagation = false,
  asChild = false,
}: TooltipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const touchOpenRef = useRef(false);
  const tooltipId = useId();

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    function handleDocumentPointerDown(event: globalThis.PointerEvent) {
      const isInsideTooltip = event
        .composedPath()
        .some(
          (target) =>
            target instanceof Element &&
            target.getAttribute("data-tooltip-id") === tooltipId,
        );

      if (!isInsideTooltip) {
        touchOpenRef.current = false;
        setIsOpen(false);
      }
    }

    document.addEventListener("pointerdown", handleDocumentPointerDown, true);
    return () => {
      document.removeEventListener(
        "pointerdown",
        handleDocumentPointerDown,
        true,
      );
    };
  }, [isOpen, tooltipId]);

  function handleTouch(pointerEvent: PointerEvent<HTMLDivElement>) {
    if (
      pointerEvent.pointerType !== "touch" ||
      isDisabled ||
      // below guard ensures that AutoRefreshSwitch and similar wrapped controls must preserve their normal touch behavior.
      pointerEvent.currentTarget.querySelector(
        "button, input, select, textarea, [role=button], [role=checkbox], [role=switch]",
      )
    ) {
      return;
    }

    touchOpenRef.current = !touchOpenRef.current;
    const nextIsOpen = touchOpenRef.current;

    // HeroUI processes its own press event after this handler. Apply the touch
    // state once that lifecycle has completed so it cannot immediately undo it.
    setTimeout(() => setIsOpen(nextIsOpen), 0);
  }

  function handleOpenChange(nextIsOpen: boolean) {
    if (!nextIsOpen && touchOpenRef.current) {
      return;
    }

    setIsOpen(nextIsOpen);
  }

  return (
    <HeroTooltip
      delay={delay}
      closeDelay={closeDelay}
      isDisabled={isDisabled}
      trigger={trigger}
      isOpen={isOpen}
      onOpenChange={handleOpenChange}
      shouldCloseOnPress={false}
    >
      {asChild ? (
        <Focusable data-tooltip-id={tooltipId}>{children}</Focusable>
      ) : (
        <HeroTooltip.Trigger
          data-tooltip-id={tooltipId}
          className={classNames?.trigger}
          onPointerDown={(event) => {
            if (stopPropagation) {
              event.preventDefault();
              event.stopPropagation();
            }
          }}
          onPointerDownCapture={(event) => {
            if (stopPropagation) {
              event.preventDefault();
              event.stopPropagation();
            }
          }}
          onPointerUp={handleTouch}
          onClick={(event) => {
            if (stopPropagation) {
              event.preventDefault();
              event.stopPropagation();
            }
          }}
          onClickCapture={(event) => {
            if (stopPropagation) {
              event.preventDefault();
              event.stopPropagation();
            }
          }}
        >
          {children}
        </HeroTooltip.Trigger>
      )}
      <HeroTooltip.Content
        data-tooltip-id={tooltipId}
        placement={placement}
        className={cn(
          "!break-normal break-words whitespace-normal",
          classNames?.content,
        )}
        showArrow={showArrow}
      >
        {showArrow ? <HeroTooltip.Arrow /> : null}
        {content}
      </HeroTooltip.Content>
    </HeroTooltip>
  );
}
